import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { API_BASE_URL } from "../utils/config";
import CustomSelect from "../components/CustomSelect";

const authHeaders = () => {
  const token = localStorage.getItem("accessToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const JOB_TYPES = [
  { value: "IOT_LAUNDRY", label: "IoT Laundry" },
  { value: "MOTOWASH", label: "Motowash" },
  { value: "OTHER", label: "Lainnya" },
];

const EMPTY_FORM = {
  outlet_name: "",
  address: "",
  job_type: "IOT_LAUNDRY",
  install_date: "",
  install_time: "",
  calibration_date: "",
  training_date: "",
  iot_channel_count: "",
  machine_detail: "",
  client_person: "",
  username_outlet_1: "",
  username_outlet_2: "",
  device_brand: "",
  device_type: "",
  device_spec: "",
  monitoring_link: "",
  am_username: "",
  subscription_start: "",
  subscription_end: "",
  notes: "",
};

function FieldGroup({ label, children }) {
  return (
    <div className="mb-5">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted mb-2">{label}</p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">{children}</div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="block text-sm font-medium text-strong mb-1">{label}</label>
      {children}
    </div>
  );
}

const isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0;

// ---------- Panel Assembly picker ----------
function AssemblyPicker({ approvedAssemblies, selectedIds, onChange, takenIds = [] }) {
  const [search, setSearch] = useState("");

  const filtered = approvedAssemblies.filter((a) => {
    const q = search.toLowerCase();
    return (
      a.reference_code.toLowerCase().includes(q) ||
      a.output_item_name?.toLowerCase().includes(q) ||
      a.output_item_code?.toLowerCase().includes(q)
    );
  });

  const toggle = (id) => {
    if (takenIds.includes(id)) return;
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((x) => x !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  const selectedItems = approvedAssemblies.filter((a) => selectedIds.includes(a.id));

  return (
    <div>
      {selectedItems.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {selectedItems.map((a) => (
            <span
              key={a.id}
              className="inline-flex items-center gap-1 rounded-full bg-brand-500/15 border border-brand-500/40 px-2.5 py-0.5 text-xs text-brand-600 dark:text-brand-400"
            >
              {a.reference_code} — {a.output_item_name}
              <button
                type="button"
                onClick={() => toggle(a.id)}
                className="opacity-70 hover:opacity-100 hover:text-red-500 ml-0.5 leading-none"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Cari panel (ref code / nama output)…"
        className="glass-input w-full mb-2"
      />
      <div className="max-h-44 overflow-y-auto rounded-xl border border-white/30 dark:border-white/10 divide-y divide-white/20 dark:divide-white/5">
        {approvedAssemblies.length === 0 && (
          <p className="px-3 py-3 text-xs text-muted">Tidak ada panel assembly yang approved.</p>
        )}
        {filtered.length === 0 && approvedAssemblies.length > 0 && (
          <p className="px-3 py-3 text-xs text-muted">Tidak ditemukan.</p>
        )}
        {filtered.map((a) => {
          const checked = selectedIds.includes(a.id);
          const taken = takenIds.includes(a.id);
          return (
            <button
              key={a.id}
              type="button"
              onClick={() => toggle(a.id)}
              disabled={taken}
              title={taken ? "Panel ini sudah dipakai di laporan lain" : undefined}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                taken
                  ? "opacity-40 cursor-not-allowed"
                  : checked
                  ? "bg-brand-500/10"
                  : "hover:bg-white/40 dark:hover:bg-white/5"
              }`}
            >
              <span
                className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center transition-colors ${
                  checked
                    ? "bg-brand-500 border-brand-500"
                    : "border-white/40 dark:border-white/20"
                }`}
              >
                {checked && (
                  <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-xs font-mono text-brand-600 dark:text-brand-400">{a.reference_code}</span>
                <span className="block text-xs text-muted truncate">
                  {a.output_item_code} — {a.output_item_name} × {a.output_quantity}
                </span>
                {taken && (
                  <span className="block text-[10px] text-yellow-600 dark:text-yellow-400">sudah dipakai di laporan lain</span>
                )}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---------- Main component ----------
function OrderPanel() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  const [formMode, setFormMode] = useState("create");
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const [form, setForm] = useState(EMPTY_FORM);
  const [photos, setPhotos] = useState([]);
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [deletePhotoIds, setDeletePhotoIds] = useState([]);

  const [adaTemuan, setAdaTemuan] = useState(false);
  const [temuan, setTemuan] = useState([]);

  const [approvedAssemblies, setApprovedAssemblies] = useState([]);
  const [selectedAssemblyIds, setSelectedAssemblyIds] = useState([]);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [uploading, setUploading] = useState(null);
  const [detailRow, setDetailRow] = useState(null);
  const [driveConflict, setDriveConflict] = useState(null);
  const fileRef = useRef();
  const cameraRef = useRef();

  const fetchReports = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE_URL}/api/installation-reports/`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((data) => { setReports(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const fetchApprovedAssemblies = useCallback(() => {
    fetch(`${API_BASE_URL}/api/panel-assemblies/?status=APPROVED`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setApprovedAssemblies(list.map((a) => ({
          id: a.id,
          reference_code: a.reference_code,
          output_item_name: a.output_item?.name || "",
          output_item_code: a.output_item?.code || "",
          output_quantity: a.output_quantity,
        })));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchReports();
    fetchApprovedAssemblies();
  }, [fetchReports, fetchApprovedAssemblies]);

  function handleField(e) { setForm((f) => ({ ...f, [e.target.name]: e.target.value })); }

  function handlePhotos(e) {
    const files = Array.from(e.target.files);
    setPhotos((prev) => [...prev, ...files.map((f) => ({ file: f, preview: URL.createObjectURL(f), caption: "" }))]);
    e.target.value = "";
  }

  function removePhoto(idx) { setPhotos((prev) => prev.filter((_, i) => i !== idx)); }
  function updateCaption(idx, val) { setPhotos((prev) => prev.map((p, i) => (i === idx ? { ...p, caption: val } : p))); }
  function markExistingPhotoForDelete(id) {
    setDeletePhotoIds((prev) => [...prev, id]);
    setExistingPhotos((prev) => prev.filter((p) => p.id !== id));
  }

  function addTemuanRow() {
    if (temuan.length >= 10) return;
    setTemuan((prev) => [...prev, { masalah: "", penyebab: "", solusi: "", status: "Selesai" }]);
  }
  function removeTemuanRow(idx) { setTemuan((prev) => prev.filter((_, i) => i !== idx)); }
  function updateTemuan(idx, field, val) { setTemuan((prev) => prev.map((t, i) => (i === idx ? { ...t, [field]: val } : t))); }

  function openCreateForm() {
    setFormMode("create"); setEditingId(null); setForm(EMPTY_FORM);
    setPhotos([]); setExistingPhotos([]); setDeletePhotoIds([]);
    setTemuan([]); setAdaTemuan(false); setSelectedAssemblyIds([]);
    setError(""); setShowForm(true);
  }

  function openEditForm(report) {
    setFormMode("edit"); setEditingId(report.id);
    const f = { ...EMPTY_FORM };
    Object.keys(EMPTY_FORM).forEach((k) => { f[k] = report[k] ?? ""; });
    if (f.install_time && f.install_time.length > 5) f.install_time = f.install_time.slice(0, 5);
    setForm(f);
    setExistingPhotos(Array.isArray(report.photos) ? report.photos : []);
    setPhotos([]); setDeletePhotoIds([]);
    const tList = Array.isArray(report.temuan_list) ? report.temuan_list : [];
    setTemuan(tList); setAdaTemuan(tList.length > 0);
    const linkedIds = Array.isArray(report.panel_assemblies)
      ? report.panel_assemblies.map((a) => (typeof a === "object" ? a.id : a))
      : [];
    setSelectedAssemblyIds(linkedIds);
    setError(""); setShowForm(true);
  }

  function closeForm() { setShowForm(false); }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    if (!form.outlet_name || !form.install_date || !form.install_time || !form.iot_channel_count) {
      setError("Nama outlet, tanggal, waktu, dan jumlah channel wajib diisi.");
      return;
    }
    const missingCaption = photos.findIndex((p) => !p.caption.trim());
    if (missingCaption !== -1) { setError(`Keterangan foto ${missingCaption + 1} wajib diisi.`); return; }
    setSubmitting(true);

    const fd = new FormData();
    const nullableFields = ["calibration_date", "training_date", "subscription_start", "subscription_end"];
    Object.entries(form).forEach(([k, v]) => {
      if (nullableFields.includes(k)) { if (v !== "") fd.append(k, v); }
      else fd.append(k, v);
    });
    selectedAssemblyIds.forEach((id) => fd.append("panel_assembly_ids", id));
    if (adaTemuan && temuan.length > 0) fd.append("temuan_list", JSON.stringify(temuan));

    if (formMode === "create") {
      photos.forEach((p) => fd.append("photos", p.file));
      photos.forEach((p) => fd.append("captions", p.caption || ""));
      try {
        const res = await fetch(`${API_BASE_URL}/api/installation-reports/`, { method: "POST", headers: authHeaders(), body: fd });
        if (!res.ok) { const d = await res.json().catch(() => null); setError(d ? JSON.stringify(d) : `Gagal mengirim data (${res.status}).`); setSubmitting(false); return; }
        closeForm(); fetchReports();
      } catch { setError("Gagal mengirim data."); }
    } else {
      deletePhotoIds.forEach((id) => fd.append("delete_photo_ids", id));
      photos.forEach((p) => fd.append("new_photos", p.file));
      photos.forEach((p) => fd.append("new_captions", p.caption || ""));
      try {
        const res = await fetch(`${API_BASE_URL}/api/installation-reports/${editingId}/`, { method: "PATCH", headers: authHeaders(), body: fd });
        if (!res.ok) { const d = await res.json().catch(() => null); setError(d ? JSON.stringify(d) : `Gagal menyimpan (${res.status}).`); setSubmitting(false); return; }
        closeForm(); fetchReports();
      } catch { setError("Gagal menyimpan perubahan."); }
    }
    setSubmitting(false);
  }

  async function handleDelete(id) {
    const res = await fetch(`${API_BASE_URL}/api/installation-reports/${id}/`, { method: "DELETE", headers: authHeaders() });
    if (res.ok || res.status === 204) { setDeleteId(null); fetchReports(); }
  }

  async function handleUploadDrive(id, force = false) {
    setUploading(id);
    const res = await fetch(`${API_BASE_URL}/api/installation-reports/${id}/generate/`, {
      method: "POST",
      headers: { ...authHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ force }),
    });
    if (res.status === 409) {
      const data = await res.json();
      setDriveConflict({ reportId: id, message: data.message, existingUrl: data.existing_url });
      setUploading(null); return;
    }
    if (res.ok) { const data = await res.json(); setReports((prev) => prev.map((r) => (r.id === id ? data : r))); }
    setUploading(null);
  }

  async function handleDownload(id, outletName, installDate) {
    setDownloading(id);
    const res = await fetch(`${API_BASE_URL}/api/installation-reports/${id}/download/`, { headers: authHeaders() });
    if (res.ok) {
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `BAST_${outletName.replace(/\s+/g, "_").substring(0, 30)}_${installDate?.replace(/-/g, "") || "nodate"}.pptx`;
      a.click();
      URL.revokeObjectURL(url);
    }
    setDownloading(null);
  }

  const jobLabel = (jt) => JOB_TYPES.find((j) => j.value === jt)?.label || jt;

  const takenIds = reports
    .filter((r) => r.id !== editingId)
    .flatMap((r) => Array.isArray(r.panel_assemblies)
      ? r.panel_assemblies.map((a) => (typeof a === "object" ? a.id : a))
      : []);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-strong">Order Panel</h1>
          <p className="text-sm text-muted mt-1">Generate laporan BAST pemasangan panel IoT</p>
        </div>
        <button onClick={openCreateForm} className="btn-primary flex items-center gap-2">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Buat Laporan Baru
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <p className="text-muted text-sm">Memuat...</p>
      ) : reports.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <p className="text-muted">Belum ada laporan. Klik "Buat Laporan Baru" untuk mulai.</p>
        </div>
      ) : (
        <div className="glass-table-wrap overflow-x-auto">
          <table className="glass-table">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left">No. Dokumen</th>
                <th className="px-4 py-3 text-left">Outlet</th>
                <th className="px-4 py-3 text-left">Jenis</th>
                <th className="px-4 py-3 text-left">Tgl Pasang</th>
                <th className="px-4 py-3 text-left">Panel</th>
                <th className="px-4 py-3 text-left">Drive</th>
                <th className="px-4 py-3 text-left">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((r) => (
                <tr key={r.id}>
                  <td className="px-4 py-3">
                    <span className="font-mono text-brand-600 dark:text-brand-400">{r.document_number}</span>
                    {r.needs_regeneration && (
                      <span className="ml-2 inline-flex items-center gap-1 badge badge-warning">
                        <span className="w-1.5 h-1.5 rounded-full bg-current inline-block" />
                        Perlu re-upload
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-strong font-medium">{r.outlet_name}</td>
                  <td className="px-4 py-3 text-muted">{jobLabel(r.job_type)}</td>
                  <td className="px-4 py-3 text-muted">{r.install_date}</td>
                  <td className="px-4 py-3">
                    {r.panel_assemblies_detail?.length > 0 ? (
                      <div className="flex flex-col gap-0.5">
                        {r.panel_assemblies_detail.map((a) => (
                          <span key={a.id} className="text-xs font-mono text-brand-600 dark:text-brand-400">
                            {a.reference_code}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-muted text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {r.drive_file_url ? (
                      <a href={r.drive_file_url} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1 text-green-600 dark:text-green-400 hover:underline text-xs font-medium">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                        Lihat di Drive
                      </a>
                    ) : (
                      <span className="text-muted text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        onClick={() => setDetailRow(r)}
                        className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-medium text-brand-700 dark:text-brand-300 bg-brand-500/10 hover:bg-brand-500/20 border border-brand-500/30 transition-colors"
                      >
                        Lihat Detail
                      </button>
                      <button
                        onClick={() => openEditForm(r)}
                        className="inline-flex items-center gap-1 rounded-lg glass border border-white/40 dark:border-white/10 px-2.5 py-1 text-xs text-strong hover:bg-white/40 dark:hover:bg-white/10 transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleUploadDrive(r.id)}
                        disabled={uploading === r.id}
                        className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-medium text-green-700 dark:text-green-300 bg-green-500/10 hover:bg-green-500/20 border border-green-500/30 disabled:opacity-50 transition-colors"
                      >
                        {uploading === r.id ? "..." : r.drive_file_url ? "Re-upload" : "Upload Drive"}
                      </button>
                      <button
                        onClick={() => handleDownload(r.id, r.outlet_name, r.install_date)}
                        disabled={downloading === r.id}
                        className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-medium text-brand-700 dark:text-brand-300 bg-brand-500/10 hover:bg-brand-500/20 border border-brand-500/30 disabled:opacity-50 transition-colors"
                      >
                        {downloading === r.id ? "..." : "↓ PPTX"}
                      </button>
                      <button
                        onClick={() => setDeleteId(r.id)}
                        className="rounded-lg px-2.5 py-1 text-xs font-medium text-red-600 dark:text-red-400 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 transition-colors"
                      >
                        Hapus
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail popup */}
      {detailRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setDetailRow(null)}>
          <div className="glass-card w-full max-w-md shadow-glass p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-bold text-strong text-base">{detailRow.outlet_name}</h3>
                <p className="text-xs text-muted font-mono">{detailRow.document_number}</p>
              </div>
              <button onClick={() => setDetailRow(null)} className="text-muted hover:text-strong ml-4 shrink-0">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Channel */}
            <div className="mb-3 flex items-center gap-2">
              <span className="text-xs text-muted w-28 shrink-0">Channel IoT</span>
              <span className="text-sm font-semibold text-strong">
                {detailRow.iot_channel_count ? `${detailRow.iot_channel_count} channel` : "-"}
              </span>
            </div>

            {/* Panels */}
            <div className="mb-4">
              <p className="text-xs text-muted mb-1.5 font-medium">Panel Terpasang</p>
              {detailRow.panel_assemblies_detail?.length > 0 ? (
                <div className="space-y-2">
                  {detailRow.panel_assemblies_detail.map((a) => (
                    <div key={a.id} className="rounded-lg bg-white/20 dark:bg-white/5 border border-white/30 dark:border-white/10 px-3 py-2">
                      <p className="text-xs font-mono font-semibold text-brand-600 dark:text-brand-400">{a.reference_code}</p>
                      <p className="text-xs text-muted">{a.output_item_code} — {a.output_item_name} × {a.output_quantity}</p>
                      <div className="flex gap-3 mt-1">
                        <span className="text-xs text-muted">
                          Gudang: <span className="text-strong">{a.location_name || "-"}</span>
                        </span>
                        <span className="text-xs text-muted">
                          Dirakit oleh: <span className="text-strong">{a.assembled_by || "-"}</span>
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted italic">Belum ada panel yang di-assign.</p>
              )}
            </div>

            {/* Link to rakitan */}
            <Link
              to="/admin/panel-assemblies"
              onClick={() => setDetailRow(null)}
              className="flex items-center justify-center gap-1.5 w-full py-2 rounded-xl bg-brand-500/10 hover:bg-brand-500/20 border border-brand-500/30 text-sm font-medium text-brand-700 dark:text-brand-300 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              Lihat Persetujuan Rakitan
            </Link>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="glass-card p-6 w-80 text-center shadow-glass">
            <p className="text-strong font-semibold mb-1">Hapus laporan ini?</p>
            <p className="text-muted text-sm mb-5">File di Drive juga akan dihapus jika ada.</p>
            <div className="flex gap-3 justify-center">
              <button onClick={() => setDeleteId(null)} className="px-4 py-2 rounded-xl glass border border-white/40 dark:border-white/10 text-strong text-sm hover:bg-white/40 dark:hover:bg-white/10 transition-colors">Batal</button>
              <button onClick={() => handleDelete(deleteId)} className="px-4 py-2 rounded-xl bg-red-600 text-white text-sm hover:bg-red-700 transition-colors">Hapus</button>
            </div>
          </div>
        </div>
      )}

      {/* Drive conflict confirm */}
      {driveConflict && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="glass-card p-6 w-96 shadow-glass">
            <p className="text-strong font-semibold mb-2">File sudah ada di Drive</p>
            <p className="text-muted text-sm mb-1">{driveConflict.message}</p>
            {driveConflict.existingUrl && (
              <a href={driveConflict.existingUrl} target="_blank" rel="noreferrer"
                className="text-xs text-brand-600 dark:text-brand-400 hover:underline block mb-4">
                Lihat file lama di Drive
              </a>
            )}
            <div className="flex gap-3 mt-4">
              <button onClick={() => setDriveConflict(null)} className="flex-1 px-4 py-2 rounded-xl glass border border-white/40 dark:border-white/10 text-strong text-sm hover:bg-white/40 dark:hover:bg-white/10 transition-colors">Batal</button>
              <button
                onClick={() => { const id = driveConflict.reportId; setDriveConflict(null); handleUploadDrive(id, true); }}
                className="flex-1 px-4 py-2 rounded-xl bg-orange-500 text-white text-sm hover:bg-orange-600 font-semibold transition-colors"
              >
                Timpa File Lama
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create / Edit slide-over */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-start justify-end bg-black/50">
          <div className="relative h-full w-full max-w-2xl glass-panel border-l border-white/30 dark:border-white/10 overflow-y-auto">
            {/* Slide-over header */}
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-white/30 dark:border-white/10 glass-panel px-6 py-4">
              <div>
                <h2 className="text-lg font-bold text-strong">
                  {formMode === "edit" ? "Edit Laporan BAST" : "Buat Laporan BAST"}
                </h2>
                {formMode === "edit" && (
                  <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-0.5">
                    Setelah simpan, klik "Re-upload" di tabel untuk update PPTX di Drive.
                  </p>
                )}
              </div>
              <button onClick={closeForm} className="text-muted hover:text-strong transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="px-6 py-5 space-y-1">

              <FieldGroup label="Info Dasar">
                <Field label="Nama Outlet *" full>
                  <input name="outlet_name" value={form.outlet_name} onChange={handleField} required className="glass-input w-full" placeholder="Outlet Moh Toha" />
                </Field>
                <Field label="Alamat" full>
                  <textarea name="address" value={form.address} onChange={handleField} rows={2} className="glass-input w-full" placeholder="Jl. Moh Toha No.1, Tangerang..." />
                </Field>
                <Field label="Jenis Pekerjaan *">
                  <CustomSelect
                    value={form.job_type}
                    onChange={(v) => handleField({ target: { name: "job_type", value: v } })}
                    options={JOB_TYPES.map((j) => ({ value: j.value, label: j.label }))}
                  />
                </Field>
                <Field label="Client Person">
                  <input name="client_person" value={form.client_person} onChange={handleField} className="glass-input w-full" placeholder="Nama PIC outlet" />
                </Field>
              </FieldGroup>

              <FieldGroup label="Jadwal Pemasangan">
                <Field label="Tanggal Pemasangan *">
                  <input type="date" name="install_date" value={form.install_date} onChange={handleField} required className="glass-input w-full" />
                </Field>
                <Field label="Waktu Pemasangan *">
                  <input type="time" name="install_time" value={form.install_time} onChange={handleField} required className="glass-input w-full" />
                </Field>
                <Field label="Tanggal Kalibrasi & Uji Coba">
                  <input type="date" name="calibration_date" value={form.calibration_date} onChange={handleField} className="glass-input w-full" />
                </Field>
                <Field label="Tanggal Pelatihan Operasional">
                  <input type="date" name="training_date" value={form.training_date} onChange={handleField} className="glass-input w-full" />
                </Field>
              </FieldGroup>

              <FieldGroup label="Detail Pemasangan">
                <Field label="Jumlah Channel IoT *">
                  <input type="number" name="iot_channel_count" value={form.iot_channel_count} onChange={handleField} required min={1} className="glass-input w-full" placeholder="20" />
                </Field>
                <Field label="Detail Mesin" full>
                  <input name="machine_detail" value={form.machine_detail} onChange={handleField} className="glass-input w-full" placeholder="Washer 10 unit, Dryer 10 unit" />
                </Field>
              </FieldGroup>

              {/* Panel Assembly picker */}
              <div className="mb-5">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted mb-2">
                  Panel yang Dipasang
                  {selectedAssemblyIds.length > 0 && (
                    <span className="ml-2 text-brand-600 dark:text-brand-400 normal-case font-normal">{selectedAssemblyIds.length} dipilih</span>
                  )}
                </p>
                <AssemblyPicker
                  approvedAssemblies={approvedAssemblies}
                  selectedIds={selectedAssemblyIds}
                  onChange={setSelectedAssemblyIds}
                  takenIds={takenIds}
                />
              </div>

              <FieldGroup label="Data Akun Outlet">
                <Field label="Username [1]">
                  <input name="username_outlet_1" value={form.username_outlet_1} onChange={handleField} className="glass-input w-full" placeholder="email@outlet.com" />
                </Field>
                <Field label="Username [2]">
                  <input name="username_outlet_2" value={form.username_outlet_2} onChange={handleField} className="glass-input w-full" placeholder="email2@outlet.com" />
                </Field>
                <Field label="Merk Device">
                  <input name="device_brand" value={form.device_brand} onChange={handleField} className="glass-input w-full" placeholder="Samsung" />
                </Field>
                <Field label="Tipe Device">
                  <input name="device_type" value={form.device_type} onChange={handleField} className="glass-input w-full" placeholder="Galaxy Tab A7 Lite" />
                </Field>
                <Field label="Spec Device" full>
                  <input name="device_spec" value={form.device_spec} onChange={handleField} className="glass-input w-full" placeholder="SM-T225" />
                </Field>
              </FieldGroup>

              <FieldGroup label="Data Akun Monitoring">
                <Field label="Link Monitoring" full>
                  <input name="monitoring_link" value={form.monitoring_link} onChange={handleField} className="glass-input w-full" placeholder="https://adp.alpha.laundry.Lisokom.com/..." />
                </Field>
                <Field label="Username AM">
                  <input name="am_username" value={form.am_username} onChange={handleField} className="glass-input w-full" placeholder="am@perusahaan.com" />
                </Field>
              </FieldGroup>

              <FieldGroup label="Subscription">
                <Field label="Mulai Subscription">
                  <input type="date" name="subscription_start" value={form.subscription_start} onChange={handleField} className="glass-input w-full" />
                </Field>
                <Field label="Akhir Subscription">
                  <input type="date" name="subscription_end" value={form.subscription_end} onChange={handleField} className="glass-input w-full" />
                </Field>
              </FieldGroup>

              <FieldGroup label="Catatan Tambahan">
                <Field label="" full>
                  <textarea name="notes" value={form.notes} onChange={handleField} rows={2} className="glass-input w-full" placeholder="Catatan tambahan jika ada..." />
                </Field>
              </FieldGroup>

              {/* Temuan Masalah */}
              <div className="mb-5">
                <div className="flex items-center gap-2 mb-2">
                  <input
                    type="checkbox"
                    id="ada-temuan"
                    checked={adaTemuan}
                    onChange={(e) => {
                      setAdaTemuan(e.target.checked);
                      if (!e.target.checked) setTemuan([]);
                      else if (temuan.length === 0) addTemuanRow();
                    }}
                    className="w-4 h-4 accent-brand-500 cursor-pointer"
                  />
                  <label htmlFor="ada-temuan" className="text-xs font-semibold uppercase tracking-wider text-muted cursor-pointer select-none">
                    Ada Temuan Masalah?
                  </label>
                </div>

                {adaTemuan && (
                  <div className="space-y-2">
                    {temuan.map((t, i) => (
                      <div key={i} className="glass-card p-3 space-y-2">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-muted font-semibold">Temuan #{i + 1}</span>
                          <button type="button" onClick={() => removeTemuanRow(i)} className="text-xs text-red-500 hover:text-red-600 dark:text-red-400">Hapus</button>
                        </div>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                          <div className="sm:col-span-2">
                            <label className="block text-xs text-muted mb-1">Temuan Masalah *</label>
                            <input type="text" value={t.masalah} onChange={(e) => updateTemuan(i, "masalah", e.target.value)} placeholder="Deskripsi masalah..." className="glass-input w-full text-xs" />
                          </div>
                          <div>
                            <label className="block text-xs text-muted mb-1">Penyebab</label>
                            <input type="text" value={t.penyebab} onChange={(e) => updateTemuan(i, "penyebab", e.target.value)} placeholder="Penyebab masalah..." className="glass-input w-full text-xs" />
                          </div>
                          <div>
                            <label className="block text-xs text-muted mb-1">Tindakan / Solusi</label>
                            <input type="text" value={t.solusi} onChange={(e) => updateTemuan(i, "solusi", e.target.value)} placeholder="Solusi yang dilakukan..." className="glass-input w-full text-xs" />
                          </div>
                          <div>
                            <label className="block text-xs text-muted mb-1">Status</label>
                            <CustomSelect
                              value={t.status}
                              onChange={(v) => updateTemuan(i, "status", v)}
                              options={[
                                { value: "Selesai",  label: "Selesai"  },
                                { value: "Pending",  label: "Pending"  },
                                { value: "Eskalasi", label: "Eskalasi" },
                              ]}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                    {temuan.length < 10 && (
                      <button type="button" onClick={addTemuanRow} className="w-full rounded-xl border-2 border-dashed border-white/30 dark:border-white/10 py-2 text-xs text-muted hover:border-brand-500/50 hover:text-brand-600 dark:hover:text-brand-400 transition-colors">
                        + Tambah Baris Temuan
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Foto Dokumentasi */}
              <div className="mb-5">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted mb-2">
                  Foto Dokumentasi
                  {(existingPhotos.length + photos.length) > 0 && (
                    <span className="ml-2 text-brand-600 dark:text-brand-400 normal-case font-normal">
                      {existingPhotos.length + photos.length} foto
                    </span>
                  )}
                </p>

                {/* Existing photos */}
                {existingPhotos.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {existingPhotos.map((p) => (
                      <div key={p.id} className="flex gap-3 items-start glass-card p-2">
                        <div className="relative group flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden bg-white/20 dark:bg-white/5">
                          <img src={`${API_BASE_URL}${p.image_url}`} alt="" className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => markExistingPhotoForDelete(p.id)}
                            className="absolute top-0.5 right-0.5 rounded-full bg-red-600 p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-muted mb-1">Foto tersimpan</p>
                          <p className="text-xs text-strong">{p.caption || <span className="italic text-muted">Tanpa keterangan</span>}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* New photos */}
                {photos.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {photos.map((p, i) => (
                      <div key={i} className="flex gap-3 items-start glass-card border-brand-500/30 p-2">
                        <div className="relative group flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden bg-white/20 dark:bg-white/5">
                          <img src={p.preview} alt="" className="w-full h-full object-cover" />
                          <button type="button" onClick={() => removePhoto(i)} className="absolute top-0.5 right-0.5 rounded-full bg-red-600 p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-muted mb-1">Foto {i + 1} baru — {p.file.name}</p>
                          <input type="text" value={p.caption} onChange={(e) => updateCaption(i, e.target.value)} placeholder="Keterangan foto (wajib)..." className="glass-input w-full text-xs" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex gap-2">
                  <div
                    onClick={() => fileRef.current?.click()}
                    className="flex-1 rounded-xl border-2 border-dashed border-white/30 dark:border-white/10 p-3 text-center cursor-pointer hover:border-brand-500/50 transition-colors"
                  >
                    <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={handlePhotos} />
                    <svg className="w-5 h-5 text-muted mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-xs text-muted">Pilih dari file</p>
                  </div>
                  {isTouchDevice && (
                    <div
                      onClick={() => cameraRef.current?.click()}
                      className="rounded-xl border-2 border-dashed border-white/30 dark:border-white/10 p-3 text-center cursor-pointer hover:border-green-500/50 transition-colors flex flex-col items-center justify-center min-w-[100px]"
                    >
                      <input ref={cameraRef} type="file" accept="image/*" capture="environment" hidden onChange={handlePhotos} />
                      <svg className="w-5 h-5 text-muted mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <p className="text-xs text-muted">Ambil Foto</p>
                    </div>
                  )}
                </div>
              </div>

              {error && (
                <div className="rounded-xl bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-600 dark:text-red-400">
                  {error}
                </div>
              )}

              <div className="sticky bottom-0 glass-panel border-t border-white/30 dark:border-white/10 pt-4 pb-2 flex gap-3 mt-4">
                <button type="button" onClick={closeForm} className="flex-1 rounded-xl glass border border-white/40 dark:border-white/10 py-2.5 text-sm text-strong hover:bg-white/40 dark:hover:bg-white/10 transition-colors">
                  Batal
                </button>
                <button type="submit" disabled={submitting} className="flex-1 btn-primary py-2.5 disabled:opacity-60">
                  {submitting
                    ? "Menyimpan..."
                    : formMode === "edit"
                    ? "Simpan Perubahan"
                    : "Simpan & Generate Laporan"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default OrderPanel;
